import { useState, useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Bot, Hash, Settings, X } from 'lucide-react'
import { useSaveTags } from '@/api/hooks/useAccounts'
import { useLanguage } from '@/i18n/LanguageContext'
import { toast } from 'sonner'
import type { Account } from '@/types'
import { Automation } from './tabs/Automation'
import { MenuOTN } from './tabs/MenuOTN'

interface Props {
  account: Account
  allTags?: string[]
  onClose?: () => void
}

export function AccountCard({ account, allTags, onClose }: Props) {
  const { t } = useLanguage()
  const [tab, setTab] = useState('auto')
  const [editingTags, setEditingTags] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const tagInputRef = useRef<HTMLInputElement>(null)
  const saveTags = useSaveTags()

  const tags = account.tags || []

  const addTag = (value: string) => {
    const tag = value.trim().toLowerCase().replace(/^#/, '').replace(/\s+/g, '-')
    if (!tag || tags.includes(tag)) { setTagInput(''); return }
    const newTags = [...tags, tag]
    saveTags.mutate({ igUserId: account.igUserId, tags: newTags, igLogin: !!(account as Record<string, unknown>).igLogin }, {
      onError: () => toast.error(t('Erro ao salvar tag')),
    })
    setTagInput('')
  }

  const removeTag = (tag: string) => {
    const newTags = tags.filter(t => t !== tag)
    saveTags.mutate({ igUserId: account.igUserId, tags: newTags, igLogin: !!(account as Record<string, unknown>).igLogin }, {
      onError: () => toast.error(t('Erro ao remover tag')),
    })
  }

  const handleTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(tagInput)
    }
    if (e.key === 'Escape') {
      setEditingTags(false)
      setTagInput('')
    }
  }

  return (
    <>
      {/* Barra de acoes */}
      <div className="flex flex-wrap items-center gap-2 bg-muted/20 px-3 py-2">

        {/* Tags */}
        <div className="flex flex-1 flex-wrap items-center gap-1">
          <Hash className="h-2.5 w-2.5 text-muted-foreground/40" />
          {tags.map(tag => (
            <span key={tag} className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] text-primary">
              #{tag}
              <button type="button" onClick={() => removeTag(tag)} className="hover:text-destructive">
                <X className="h-2 w-2" />
              </button>
            </span>
          ))}
          {editingTags ? (
            <>
              <Input
                ref={tagInputRef}
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKey}
                onBlur={() => { if (tagInput) addTag(tagInput); setEditingTags(false) }}
                placeholder="tag + Enter"
                className="h-4 w-20 border-none bg-transparent px-1 text-[9px] shadow-none focus-visible:ring-0"
              />
              {allTags && allTags.filter(t => !tags.includes(t)).length > 0 && (
                <>
                  <span className="text-[8px] text-muted-foreground/40">|</span>
                  {allTags.filter(t => !tags.includes(t)).slice(0, 8).map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); addTag(tag) }}
                      className="rounded-full bg-muted/30 px-1.5 py-0.5 text-[8px] text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                    >
                      +{tag}
                    </button>
                  ))}
                </>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setEditingTags(true); setTimeout(() => tagInputRef.current?.focus(), 50) }}
              className="rounded px-1 py-0.5 text-[9px] text-muted-foreground/50 transition-colors hover:bg-muted hover:text-muted-foreground"
            >
              + tag
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <div className="border-t border-border px-1">
          <TabsList className="h-auto gap-0 bg-transparent p-0">
            <TabsTrigger value="auto" className="gap-1 rounded-none border-b-2 border-transparent px-3 py-2 text-[11px] data-[state=active]:border-purple-400 data-[state=active]:bg-transparent data-[state=active]:shadow-none">
              <Bot className="h-3 w-3" /> Auto
            </TabsTrigger>
            <TabsTrigger value="menu" className="gap-1 rounded-none border-b-2 border-transparent px-3 py-2 text-[11px] data-[state=active]:border-muted-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none">
              <Settings className="h-3 w-3" /> Config
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="p-4">
          <TabsContent value="auto" className="m-0">
            <Automation account={account} onSaved={onClose} />
          </TabsContent>
          <TabsContent value="menu" className="m-0">
            <MenuOTN account={account} onSaved={onClose} />
          </TabsContent>
        </div>
      </Tabs>
    </>
  )
}
